import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ErrorApi } from './lib/api'
import { ProveedorTiempoReal, useCanal } from './lib/tiempoReal'
import { useRuta, uuidDeFicha } from './lib/ruta'
import { BarraLateral } from './componentes/BarraLateral'
import { Entrar } from './paginas/Entrar'
import { Ficha } from './paginas/Ficha'
import { Sala } from './paginas/Sala'
import { Backoffice } from './paginas/Backoffice'
import { Contratos } from './paginas/Contratos'
import { Bajas } from './paginas/Bajas'
import { Oportunidades } from './paginas/Oportunidades'
import { Liquidaciones } from './paginas/Liquidaciones'
import { Metricas } from './paginas/Metricas'
import { Personal } from './paginas/Personal'
import { puede, puedeAlgo } from './lib/permisos'
import type { Usuario } from './lib/tipos'

export function App() {
  const sesion = useQuery({
    queryKey: ['yo'],
    queryFn: () => api<{ usuario: Usuario }>('/api/auth/yo'),
  })

  if (sesion.isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-sm text-slate-400">
        Cargando…
      </div>
    )
  }

  if (sesion.error instanceof ErrorApi && sesion.error.estado === 401) {
    return <Entrar />
  }

  if (!sesion.data) {
    return (
      <div className="flex min-h-dvh items-center justify-center px-4 text-center text-sm text-red-600">
        No se ha podido contactar con el servidor.
      </div>
    )
  }

  return (
    <ProveedorTiempoReal>
      <Paginas usuario={sesion.data.usuario} />
    </ProveedorTiempoReal>
  )
}

function Paginas({ usuario }: { usuario: Usuario }) {
  const { ruta, ir } = useRuta()
  const clienteQuery = useQueryClient()
  const uuid = uuidDeFicha(ruta)

  // Backoffice cambia un precio o un final y todo el mundo lo ve al momento.
  useCanal('catalogo', () => {
    void clienteQuery.invalidateQueries({ queryKey: ['catalogos'] })
    void clienteQuery.invalidateQueries({ queryKey: ['backoffice-catalogo'] })
  })

  // Mismo reparto que el menú: si alguien escribe la ruta a mano, tampoco entra.
  // Quien manda de verdad es el servidor; esto solo evita pantallas vacías.

  // El menú es una columna a la izquierda; arriba solo van las cosas de la
  // llamada que se está gestionando. `min-w-0` para que las tablas anchas se
  // desplacen dentro del contenido en vez de estirar la página entera.
  return (
    <div className="min-h-dvh md:flex">
      <BarraLateral usuario={usuario} ruta={ruta} ir={ir} />
      {/* El ancho y el margen de las pantallas se fijan aquí y solo aquí: si cada
          página trae los suyos, acaban descuadradas unas respecto a otras. */}
      <main className="w-full min-w-0 flex-1 px-4 py-6">
        {uuid ? (
          <Ficha uuid={uuid} usuario={usuario} ir={ir} />
        ) : ruta.startsWith('/bajas') ? (
          // Bajas tiene su propia pantalla desde el 15 de octubre: «cuando entra
          // un contrato en bajas aparecería en el apartado de bajas, no en
          // contratos».
          <Bajas usuario={usuario} ir={ir} />
        ) : ruta.startsWith('/oportunidades') && puede(usuario, 'oportunidades') ? (
          <Oportunidades ir={ir} />
        ) : ruta.startsWith('/sala') && puede(usuario, 'sala') ? (
          <Sala />
        ) : ruta.startsWith('/liquidaciones') && puede(usuario, 'liquidaciones') ? (
          <Liquidaciones usuario={usuario} />
        ) : ruta.startsWith('/metricas') && puede(usuario, 'metricas') ? (
          <Metricas usuario={usuario} />
        ) : ruta.startsWith('/mantenimiento') && puede(usuario, 'mantenimiento') ? (
          <Backoffice usuario={usuario} />
        ) : ruta.startsWith('/personal') && puedeAlgo(usuario, 'personal') ? (
          <Personal usuario={usuario} />
        ) : (
          // Llamadas y contratos son ya un solo apartado, y es el de entrada.
          <Contratos usuario={usuario} ir={ir} />
        )}
      </main>
    </div>
  )
}
